// =============================================================================
// domain/commissionEditor.ts — editing one firm's commission rates.
// -----------------------------------------------------------------------------
// `commissions.ts` READS a schedule and turns it into a fee. This is the other
// direction: the rows a trader types, validated, on their way to the one row per
// (user, firm) that the database keeps.
//
// ── 🚨 THIS IS THE MOST FAR-REACHING WRITE ON THE ACCOUNTS TAB ──
//
// A schedule belongs to a FIRM, not an account. Editing Apex's ES rate moves the
// net P&L of every ES trade on every Apex account the trader has ever had —
// including blown and archived ones, because the fee is derived on read (see
// `selectors/derive.ts`). Nothing else on this screen reaches past one account.
//
// The direction of a mistake matters too. A rate typed too LOW subtracts too
// little, and every figure downstream — day P&L, the equity curve, profit
// factor, drawdown room, every prop compliance gauge — reads BETTER than the
// trader's real result. That is the failure that does not look like one.
//
// ── ⚠ ROOT SYMBOLS, ALWAYS ──
//
// Rates are keyed by root (`ES`, not `ESM6`), because contracts roll and a rate
// filed under an expiry would stop matching in three months' time and silently
// go back to charging nothing.
// =============================================================================

import { normalizeFirm, rootSymbol, type CommissionSchedule } from './commissions';

export { normalizeFirm, rootSymbol };

/** One editable line: an instrument and its per-SIDE rate, as typed. */
export interface RateRow {
  /** Always a root symbol, upper-cased. */
  symbol: string;
  /** ⚠ THE RAW STRING. A half-typed "1." must survive a re-render. */
  input: string;
}

export interface RateRowError {
  symbol: string;
  message: string;
}

/** A schedule as the editor holds it, before it becomes a database row. */
export interface CommissionDraft {
  /** What the trader typed in the Firm box — the display name. */
  firm: string;
  rows: RateRow[];
}

/** The rows for one firm, sorted, from whatever is already stored. */
export function draftFromSchedule(
  firm: string, schedule: CommissionSchedule | undefined,
): CommissionDraft {
  const rates = schedule?.rates ?? {};
  const rows = Object.keys(rates)
    .sort()
    .map((sym) => ({ symbol: sym, input: String(rates[sym] ?? 0) }));
  return { firm, rows };
}

/**
 * Add an instrument at zero, or leave the draft alone if it is already there.
 *
 * ⚠ ZERO, NOT BLANK. A blank row would validate as an error the moment it
 * appears, and an error the user has not caused yet is noise. Zero is also the
 * honest starting value: it is what an unlisted instrument already costs.
 */
export function addRate(draft: CommissionDraft, symbol: string): CommissionDraft {
  const sym = rootSymbol(symbol);
  if (!sym || draft.rows.some((r) => r.symbol === sym)) return draft;
  return {
    ...draft,
    rows: [...draft.rows, { symbol: sym, input: '0' }]
      .sort((a, b) => a.symbol.localeCompare(b.symbol)),
  };
}

export function removeRate(draft: CommissionDraft, symbol: string): CommissionDraft {
  return { ...draft, rows: draft.rows.filter((r) => r.symbol !== symbol) };
}

export function setRate(
  draft: CommissionDraft, symbol: string, input: string,
): CommissionDraft {
  return {
    ...draft,
    rows: draft.rows.map((r) => (r.symbol === symbol ? { ...r, input } : r)),
  };
}

/**
 * What is wrong with the typed rates.
 *
 * ⚠ AN EMPTY ROW IS AN ERROR, NOT A ZERO. "I cleared this box" and "this
 * instrument is free" are different intentions, and quietly reading the first as
 * the second files a $0 rate that will never charge anything again.
 */
export function validateDraft(draft: CommissionDraft): RateRowError[] {
  const out: RateRowError[] = [];
  for (const r of draft.rows) {
    const s = r.input.trim();
    if (!s) {
      out.push({ symbol: r.symbol, message: 'Enter a rate, or remove the row.' });
      continue;
    }
    const n = Number(s);
    if (!Number.isFinite(n)) {
      out.push({ symbol: r.symbol, message: 'That is not a number.' });
      continue;
    }
    if (n < 0) {
      out.push({ symbol: r.symbol, message: 'A commission cannot be negative.' });
      continue;
    }
    // ⚠ A CEILING, AND IT IS DELIBERATELY GENEROUS. Real futures commissions run
    // well under $10 a side; $100 is far past any of them and is what a
    // fat-fingered decimal point looks like. It warns rather than guessing where
    // the point belonged — an exotic contract could legitimately be dear.
    if (n > 100) {
      out.push({
        symbol: r.symbol,
        message: 'That is over $100 a side — check the decimal point.',
      });
    }
  }
  return out;
}

/** Errors that must stop a save. The over-$100 warning is not one of them. */
export function blockingRateErrors(errors: readonly RateRowError[]): RateRowError[] {
  return errors.filter((e) => !e.message.startsWith('That is over'));
}

/** The rates map to store, keyed by root symbol. Invalid rows are dropped. */
export function ratesFromDraft(draft: CommissionDraft): Record<string, number> {
  const out: Record<string, number> = {};
  for (const r of draft.rows) {
    const n = Number(r.input.trim());
    if (r.input.trim() && Number.isFinite(n) && n >= 0) out[rootSymbol(r.symbol)] = n;
  }
  return out;
}

/**
 * A round turn is the rate twice — in and out.
 *
 * ⚠ IT IS SHOWN BESIDE EVERY ROW BECAUSE THE PER-SIDE NUMBER IS THE ONE PEOPLE
 * GET WRONG. Firms quote both, sometimes in the same table, and a round-turn
 * figure typed into a per-side box doubles every fee the app will ever charge.
 */
export function roundTurn(perSide: number): number {
  return perSide * 2;
}

/** Has anything actually changed against what is stored? */
export function draftIsDirty(
  draft: CommissionDraft, schedule: CommissionSchedule | undefined,
): boolean {
  const stored = schedule?.rates ?? {};
  const next = ratesFromDraft(draft);
  const keys = new Set([...Object.keys(stored), ...Object.keys(next)]);
  for (const k of keys) if (stored[k] !== next[k]) return true;
  return false;
}
